import { FormEditTabType } from '@doss/common-all'
import { FormFieldType } from '../../common/generated/apollo-operations'
import {
  DetailedForm,
  DetailedFormCategory,
  DetailedFormField,
  DetailedFormPage,
  NonNullableDetailedFormCategoryLookupTable,
} from '../forms/types'
import { LayoutTypes, RightPanelObjectType, RightPanelOperation } from './constants'
import { DropTargetEdge } from './hooks'

export type CreatePanelState = {
  form: DetailedForm
  page?: DetailedFormPage
  category?: DetailedFormCategory
  field?: DetailedFormField
  type?: LayoutTypes | FormFieldType
  edge?: DropTargetEdge
  targetId?: string
}

export type EditPagePanelState = {
  page: DetailedFormPage
}

export type EditCategoryPanelState = {
  category: DetailedFormCategory
}

export type EditFormFieldPanelState = {
  field: DetailedFormField
  category: DetailedFormCategory
}

export type DeleteFormPanelState = {
  operation: RightPanelOperation.DELETE
  objectType: RightPanelObjectType.FORM
  relevantLookupTables: NonNullableDetailedFormCategoryLookupTable[]
}

export type UpdateFormIsSharedPanelState = {
  operation: RightPanelOperation.UPDATE
  makeShareable: boolean
  relevantLookupTables: NonNullableDetailedFormCategoryLookupTable[]
}
export type CreateWorkspaceButtonPanelState = {
  operation: RightPanelOperation.CREATE
  objectType: RightPanelObjectType.WORKSPACE_BUTTON
}

export type EditWorkspaceButtonPanelState = {
  operation: RightPanelOperation.UPDATE
  objectType: RightPanelObjectType.WORKSPACE_BUTTON
  objectId: string
}

/**
 * Checks if the right panel state is for creating a page or category or field by ensuring it has
 * a form property
 */
export function isCreatePanelState(state: RightPanelState): state is CreatePanelState {
  return 'form' in state && !!state.form.id
}

/**
 * Checks if the right panel state is for editing a form page by checking that the state has a
 * page property and no form property
 */
export function isEditPagePanelState(state: RightPanelState): state is EditPagePanelState {
  return !('form' in state) && 'page' in state && !!state.page.id
}

/**
 * Checks if the right panel state is for editing a form category by checking that the state has
 * a category property but no form or field property
 */
export function isEditCategoryPanelState(state: RightPanelState): state is EditCategoryPanelState {
  return !('form' in state) && !('field' in state) && 'category' in state && !!state.category.id
}

/**
 * Checks if the right panel state is for editing a form field by checking that the state has
 * a field and category property but no form propery.  The category is required to check if
 * the form field updates are valid
 */
export function isEditFormFieldPanelState(
  state: RightPanelState,
): state is EditFormFieldPanelState {
  return (
    !('form' in state) &&
    'field' in state &&
    !!state.field.id &&
    'category' in state &&
    !!state.category
  )
}

/**
 * Checks that the right panel state is for updating a form's isShareable property
 */
export function isUpdateFormIsShareablePanelState(
  state: RightPanelState,
): state is UpdateFormIsSharedPanelState {
  return 'makeShareable' in state
}

/**
 * Checks that the right panel state is for deleting a form by checking that the operation
 * is DELETE and the property objectType is present
 */
export function isDeleteFormPanelState(state: RightPanelState): state is DeleteFormPanelState {
  return (
    'operation' in state && 'objectType' in state && state.operation === RightPanelOperation.DELETE
  )
}

/**
 * Checks that the right panel state is for creating a workspace button by ensuring that the
 * operation is CREATE and that the objectType property is present
 */
export function isCreateWorkspaceButtonPanelState(
  state: RightPanelState,
): state is CreateWorkspaceButtonPanelState {
  return (
    'operation' in state && 'objectType' in state && state.operation === RightPanelOperation.CREATE
  )
}

/**
 * Checks that the right panel state is for editing a workspace button by ensuring that the objectId
 * property is present
 */
export function isEditWorkspaceButtonPanelState(
  state: RightPanelState,
): state is EditWorkspaceButtonPanelState {
  return 'operation' in state && 'objectType' in state && 'objectId' in state
}

/**
 * Checks that the right panel state is for deleting a form by checking that the operation
 * is DELETE and the property objectType is present
 */
export function isEmptyContentPanelState(
  state: RightPanelState | null,
  currentSettingsTab: FormEditTabType,
): state is null {
  return state === null && currentSettingsTab !== FormEditTabType.FORM_CONTENT
}

export type RightPanelState =
  | CreatePanelState
  | EditPagePanelState
  | EditCategoryPanelState
  | EditFormFieldPanelState
  | DeleteFormPanelState
  | UpdateFormIsSharedPanelState
  | CreateWorkspaceButtonPanelState
  | EditWorkspaceButtonPanelState

export enum FormSettingsDropTargetType {
  FORM_OUTLINE = 'FORM_OUTLINE',
  PAGE_IN_OUTLINE = 'PAGE_IN_OUTLINE',
  PAGE_IN_EDITOR = 'PAGE_IN_EDITOR',
  CATEGORY_IN_PAGE = 'CATEGORY_IN_PAGE',
  FIELD_IN_CATEGORY = 'FIELD_IN_CATEGORY',
}

export enum FormSettingsSourceType {
  PAGE = 'PAGE',
  CATEGORY = 'CATEGORY',
  FIELD = 'FIELD',
  EMPTY_PAGE = 'EMPTY_PAGE',
  EMPTY_CATEGORY = 'EMPTY_CATEGORY',
  EMPTY_FIELD = 'EMPTY_FIELD',
}

export enum FormSettingsDragAndDropType {
  CREATE_PAGE_ON_PAGE = 'CREATE_PAGE_ON_PAGE',
  CREATE_PAGE_ON_OUTLINE = 'CREATE_PAGE_ON_OUTLINE',
  CREATE_CATEGORY_ON_PAGE = 'CREATE_CATEGORY_ON_PAGE',
  CREATE_CATEGORY_ON_CATEGORY = 'CREATE_CATEGORY_ON_CATEGORY',
  CREATE_FIELD_ON_CATEGORY = 'CREATE_FIELD_ON_CATEGORY',
  CREATE_FIELD_ON_FIELD = 'CREATE_FIELD_ON_FIELD',
  MOVE_PAGE_ON_PAGE = 'MOVE_PAGE_ON_PAGE',
  MOVE_PAGE_ON_OUTLINE = 'MOVE_PAGE_ON_OUTLINE',
  MOVE_CATEGORY_ON_PAGE = 'MOVE_CATEGORY_ON_PAGE',
  MOVE_CATEGORY_ON_CATEGORY = 'MOVE_CATEGORY_ON_CATEGORY',
  MOVE_FIELD_ON_CATEGORY = 'MOVE_FIELD_ON_CATEGORY',
  MOVE_FIELD_ON_FIELD = 'MOVE_FIELD_ON_FIELD',
}
